// REAL. The methodology toolkit quiz — the real onboarding front (Rhys spawn
// 2026-06-03), replacing the free-text extract-toolkit skeleton and its
// circular placeholder.
//
// Shape: a warm welcome frame → 5 questions, one beat per question (page-arc,
// forward motion per the gate's page-arc decision) → on the final answer,
// POST /quiz-result places the actor deterministically into a lineage. The
// reveal ("you're in good company") lands on the next screen (/toolkit).
//
// Register (Cael, recognition-not-classification): the frame says we're
// getting to know how the actor works — not running them through a methodology
// selector. The questions are about what the ACTOR does. No tap feels like
// ticking a box; each is a real choice about their craft. Question + option
// text is sourced from the engine (the quiz doc), never invented here.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getQuiz, postQuizResult, QuizPayload } from "../api/client";
import { useArc } from "../state/arc";

export default function WelcomeBeats() {
  const navigate = useNavigate();
  const { state, update } = useArc();
  const [quiz, setQuiz] = useState<QuizPayload | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // step 0 = welcome frame; steps 1..N = questions; we submit after the last.
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>(
    state.quizAnswers ?? {},
  );
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    getQuiz()
      .then(setQuiz)
      .catch((e) =>
        setLoadError(
          e?.message ?? "Couldn't load the welcome. Check your connection?",
        ),
      );
  }, []);

  if (loadError) {
    return (
      <section>
        <h2>One sec.</h2>
        <p className="muted">{loadError}</p>
        <button
          onClick={() => {
            setLoadError(null);
            getQuiz().then(setQuiz).catch((e) => setLoadError(e?.message));
          }}
        >
          Try again
        </button>
      </section>
    );
  }

  if (!quiz) {
    return (
      <section>
        <h2>Welcome to Off Book.</h2>
        <p className="muted">Setting up…</p>
      </section>
    );
  }

  const questions = quiz.questions;
  const total = questions.length;

  // --- Welcome frame (step 0) ------------------------------------------------
  if (step === 0) {
    return (
      <section>
        <h2>Welcome to Off Book.</h2>
        <p>
          This is where the work happens before the room — before the
          audition, the rehearsal, the set. Just you and the part.
        </p>
        <p>
          Before your first project, a few questions about how you actually
          work. There are no wrong answers — they just let us set things up the
          way you do.
        </p>
        <button onClick={() => setStep(1)}>Let's begin →</button>
      </section>
    );
  }

  // --- Questions (steps 1..total) -------------------------------------------
  const q = questions[step - 1];
  const selected = answers[q.id] ?? null;
  const isLast = step === total;

  function choose(optionKey: string) {
    setAnswers((prev) => {
      const next = { ...prev, [q.id]: optionKey };
      update({ quizAnswers: next });
      return next;
    });
  }

  async function onNext() {
    if (!selected) return;
    if (!isLast) {
      setStep((s) => s + 1);
      return;
    }
    // Final answer — place the actor.
    setBusy(true);
    setSubmitError(null);
    try {
      const result = await postQuizResult(answers);
      update({
        quizResult: result,
        // Mirror the lineage's analyzer methodology so the downstream arc
        // (/confirm → /analyze) is unchanged. The actor never sees this id.
        methodologyId: result.analyzer_methodology_id,
      });
      navigate("/toolkit");
    } catch (e: any) {
      setSubmitError(
        e?.message ?? "Couldn't place your answers just now. Try again?",
      );
    } finally {
      setBusy(false);
    }
  }

  function onBack() {
    setSubmitError(null);
    setStep((s) => Math.max(0, s - 1));
  }

  return (
    <section>
      <p className="muted">
        {step} of {total}
      </p>
      <h2 className="q">{q.prompt}</h2>
      <div>
        {q.options.map((o) => {
          const active = selected === o.key;
          return (
            <button
              key={o.key}
              onClick={() => choose(o.key)}
              className="card"
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                marginBottom: "0.6rem",
                borderColor: active ? "var(--accent)" : "var(--border)",
              }}
            >
              <strong>{o.label}</strong>
              <span
                className="muted"
                style={{ display: "block", marginTop: "0.25rem" }}
              >
                {o.body}
              </span>
            </button>
          );
        })}
      </div>

      {submitError && (
        <p className="muted" style={{ color: "#e08383" }}>
          {submitError}
        </p>
      )}

      <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
        <button
          onClick={onBack}
          style={{ background: "transparent" }}
          disabled={busy}
        >
          ← Back
        </button>
        <button onClick={onNext} disabled={!selected || busy}>
          {busy
            ? "Reading you…"
            : isLast
              ? "See where you land →"
              : "Next →"}
        </button>
      </div>
    </section>
  );
}
